<script lang="ts" setup>

import { CartStateEnum, useCartStore } from '@/stores/cart/useCartStore';
import { useTransactionStore } from '@/stores/transaction/useTransactionStore';
import { formatCurrency } from '@/utils/helpers';
import { computed, storeToRefs, useUserStore } from '#imports';

const transactionStore = useTransactionStore();
const userStore = useUserStore();

const { transactions } = storeToRefs(transactionStore);

const cartStore = useCartStore();

const images = computed(() => transactions.value.map(item => item.tokenList.map(image => image.displayImageURL)).flat());

const summ = computed(() => transactions.value.reduce(
    (acc, item) => acc + item.value,
    0
));

const getCardStyle = (index: number) => {
    const maxRotation = 10;
    const rotation = (index - (transactions.value.length / 2)) * (maxRotation / transactions.value.length);
    const offset = 20;
    const translateX = (index - (transactions.value.length / 2)) * offset;

    return {
        transform: `rotate(${rotation}deg) translateX(${translateX}px)`,
        zIndex: index
    };
};

const currencyType = transactions.value[0]
    ? transactions.value[0].currency
    : 'eth';

const formattedPrice = computed(() => formatCurrency({
    value: BigInt(String(summ.value)),
    currencyType
}));

const handleClose = () => {
    cartStore.setCartState({
        visible: false,
        state: CartStateEnum.CART
    });
    transactionStore.clearTransactions();
};

</script>

<template>
  <div class="display-flex flex-column align-items-center gap-24">
    <div>
      <h3 class="compact-500-accent">
        These NFTs are yours!
      </h3>
      <p class="text-400-default text-color-secondary text-center">
        Successful payment
      </p>
    </div>

    <div class="list">
      <div v-for="(image, index) in images"
           :key="index"
           class="list__card"
           :style="getCardStyle(index)"
      >
        <ImgMain :src="image"
                 alt="status"
        />
      </div>
    </div>
    <div class="display-flex justify-between align-self-stretch text-color-secondary compact-400-accent">
      <span>Price</span>
      <span>{{ formattedPrice }} {{ currencyType.toUpperCase() }}</span>
    </div>
    <div class="display-flex justify-between align-self-stretch compact-500-accent">
      <span>Total paid</span>
      <span>{{ formattedPrice }} {{ currencyType.toUpperCase() }}</span>
    </div>
    <div class="display-flex align-self-stretch">
      <Button size="size.medium">
        <NuxtLink :to="`/profile/${userStore.user}?network=${currencyType}`"
                  class="compact-500-accent"
                  @click="handleClose"
        >
          To inventory
        </NuxtLink>
      </Button>
      <Button size="size.medium"
              :color="'bg.text'"
              class="grow"
              @click="handleClose"
      >
        <button class="compact-500-accent"
                type="button"
        >
          Close
        </button>
      </Button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.list {
  position: relative;
  width: 120px;
  height: 120px;

  &__card {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: var(--control-400-radius, 2px);
    background-color: var(--bg-secondary);
    box-shadow: 0 2px 100px rgb(255 255 255 / 20%);
  }
}
</style>
